import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  CircularProgress,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import ShareIcon from "@mui/icons-material/Share";
import { Group, RecordShare, SharePermission } from "../../lib/types";
import { shareAPI } from "../../services/api";

interface ManageSharesModalProps {
  open: boolean;
  title: string;
  // Account ids in scope. For an account it's one; for a bank/org it's all of
  // that group's accounts. Sharing applies across the whole set.
  accountIds: string[];
  groups: Group[];
  onClose: () => void;
  onChanged?: () => void;
}

const ManageSharesModal: React.FC<ManageSharesModalProps> = ({
  open,
  title,
  accountIds,
  groups,
  onClose,
  onChanged,
}) => {
  const [loading, setLoading] = useState(false);
  const [busyGroupId, setBusyGroupId] = useState<string | null>(null);
  // shares keyed by accountId.
  const [sharesByAccount, setSharesByAccount] = useState<
    Record<string, RecordShare[]>
  >({});

  const loadShares = useCallback(async () => {
    if (accountIds.length === 0) return;
    setLoading(true);
    try {
      const results = await Promise.all(
        accountIds.map((id) => shareAPI.getSharesForRecord("account", id))
      );
      const map: Record<string, RecordShare[]> = {};
      accountIds.forEach((id, i) => {
        map[id] = results[i] || [];
      });
      setSharesByAccount(map);
    } catch (err) {
      console.error("[ManageSharesModal] failed to load shares", err);
    } finally {
      setLoading(false);
    }
  }, [accountIds]);

  useEffect(() => {
    if (open) loadShares();
  }, [open, loadShares]);

  // Per-group sharing state across all accounts in scope.
  const groupState = useMemo(() => {
    return groups.map((group) => {
      const sharedAccountIds = accountIds.filter((id) =>
        (sharesByAccount[id] || []).some((s) => s.groupId === group.id)
      );
      const anyShare = accountIds
        .flatMap((id) => sharesByAccount[id] || [])
        .find((s) => s.groupId === group.id);
      return {
        group,
        sharedCount: sharedAccountIds.length,
        total: accountIds.length,
        fullyShared:
          sharedAccountIds.length === accountIds.length &&
          accountIds.length > 0,
        partiallyShared:
          sharedAccountIds.length > 0 &&
          sharedAccountIds.length < accountIds.length,
        permission: (anyShare?.permission || "view") as SharePermission,
      };
    });
  }, [groups, accountIds, sharesByAccount]);

  const sharedSummary = groupState
    .filter((g) => g.sharedCount > 0)
    .map((g) => g.group.name);

  const toggleGroup = async (
    groupId: string,
    nextShared: boolean,
    permission: SharePermission
  ) => {
    setBusyGroupId(groupId);
    try {
      if (nextShared) {
        // Share every account not already shared with this group.
        const toShare = accountIds.filter(
          (id) =>
            !(sharesByAccount[id] || []).some((s) => s.groupId === groupId)
        );
        if (toShare.length > 0) {
          await Promise.all(
            toShare.map((id) =>
              shareAPI.shareRecord({
                recordType: "account",
                recordId: id,
                groupIds: [groupId],
                permission,
              })
            )
          );
        }
      } else {
        // Remove every existing share with this group.
        const shareIds = accountIds.flatMap((id) =>
          (sharesByAccount[id] || [])
            .filter((s) => s.groupId === groupId)
            .map((s) => s.id)
        );
        await Promise.all(shareIds.map((sid) => shareAPI.unshare(sid)));
      }
      await loadShares();
      onChanged?.();
    } catch (err) {
      console.error("[ManageSharesModal] toggle failed", err);
    } finally {
      setBusyGroupId(null);
    }
  };

  const changePermission = async (
    groupId: string,
    permission: SharePermission
  ) => {
    // Re-share with the new permission (server upserts by record+group).
    setBusyGroupId(groupId);
    try {
      await Promise.all(
        accountIds.map((id) =>
          shareAPI.shareRecord({
            recordType: "account",
            recordId: id,
            groupIds: [groupId],
            permission,
          })
        )
      );
      await loadShares();
      onChanged?.();
    } catch (err) {
      console.error("[ManageSharesModal] permission change failed", err);
    } finally {
      setBusyGroupId(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <ShareIcon />
        Sharing — {title}
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {accountIds.length === 1
            ? "Choose which groups can see this account."
            : `Choose which groups can see these ${accountIds.length} accounts.`}
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Currently shared with:
          </Typography>{" "}
          {sharedSummary.length > 0 ? (
            <Box sx={{ display: "inline-flex", flexWrap: "wrap", gap: 0.5 }}>
              {sharedSummary.map((name) => (
                <Chip key={name} label={name} size="small" color="success" />
              ))}
            </Box>
          ) : (
            <Typography variant="caption">no one yet</Typography>
          )}
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={22} />
          </Box>
        ) : groups.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            You don't have any groups yet. Create a group (Manage Organizations
            / Groups) and invite people, then share accounts with it.
          </Typography>
        ) : (
          <List dense disablePadding>
            {groupState.map((gs) => (
              <ListItem
                key={gs.group.id}
                secondaryAction={
                  (gs.fullyShared || gs.partiallyShared) && (
                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      value={gs.permission}
                      onChange={(_, val) =>
                        val && changePermission(gs.group.id, val)
                      }
                      disabled={busyGroupId === gs.group.id}
                    >
                      <ToggleButton value="view" sx={{ py: 0.25, px: 1 }}>
                        View
                      </ToggleButton>
                      <ToggleButton value="edit" sx={{ py: 0.25, px: 1 }}>
                        Edit
                      </ToggleButton>
                    </ToggleButtonGroup>
                  )
                }
              >
                <Checkbox
                  edge="start"
                  checked={gs.fullyShared}
                  indeterminate={gs.partiallyShared}
                  disabled={busyGroupId === gs.group.id}
                  onChange={(e) =>
                    toggleGroup(gs.group.id, e.target.checked, gs.permission)
                  }
                />
                <ListItemText
                  primary={gs.group.name}
                  secondary={
                    gs.partiallyShared
                      ? `Shared with ${gs.sharedCount} of ${gs.total}`
                      : gs.fullyShared
                        ? "Shared"
                        : "Not shared"
                  }
                />
                {busyGroupId === gs.group.id && (
                  <CircularProgress size={14} sx={{ ml: 1 }} />
                )}
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Done</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManageSharesModal;
